import React, { useEffect, useMemo, useRef, useState } from 'react';
import { p } from '../../i18n/useP';
import { fmtMonthYear } from './peopleMonthly';
import './PlanningTimeline.css';

/* Frise de planification — proposition validée le 2026-08-21, roulante depuis
   le 2026-08-22.

   Le tableau à colonnes n'a PAS D'AXE DU TEMPS : chaque carte y réimprimait
   ses dates, et sept changements de taux faisaient une colonne interminable.
   Or la date est l'information principale d'un mouvement.

   ELLE ROULE SUR N ET N+1. L'exercice sélectionné est le point de DÉPART, plus
   le point d'arrêt : un contrat qui se termine en septembre N+1 était invisible
   (chez KeyIT, 3,6 EPT de baisse déjà signés ne se voyaient nulle part). Chaque
   année garde sa frontière — un bandeau qui la nomme, un filet plein qui la
   sépare. Combien d'années à l'écran ? La LARGEUR décide : deux si elle les
   porte, une sinon, et les flèches font le reste. Un réglage de moins.

   TROIS LECTURES du même axe :
     · PAR PERSONNE   — qui bouge. Une case par mois qui porte son taux, et
       deux signaux qui ne se recouvrent pas :
         le FOND     gris = taux au contrat · ardoise = mois SIMULÉ (le taux
                     s'écarte du contrat), sur toute la durée du passage ;
         le FILET    à la frontière du mois où le taux bascule.
       La modification de SALAIRE n'y porte plus de marqueur (2026-08-26) : le
       point se posait sur une case dont il ne changeait pas le chiffre.
     · PAR DÉPARTEMENT — combien. Un remplacement (un départ + une embauche)
       fait deux lignes qui bougent en lecture nominative, alors que l'effectif
       n'a pas bougé d'un EPT. Agrégé, il ne fait aucune vague : c'est là qu'on
       voit une vraie hausse ou une vraie baisse. Le ＋ y pose un poste vacant
       DANS ce département — donc avec son coût de référence.
     · PAR CAUSE      — ce que j'ai décidé, et quand. Une barre par mouvement.

   Lecture seule : la saisie reste au tableau (une colonne par nature). */

const MOIS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun',
              'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];

/* Un mois « simulé » = le taux diffère de la baseline contractuelle. C'est la
   seule définition robuste : elle ne dépend pas du type de mouvement, donc un
   futur type y entre sans code supplémentaire.

   Une LIGNE SYNTHÉTIQUE (position ouverte, réduction générale) n'a pas de
   baseline : elle n'existe que PAR le mouvement. Tout mois où elle porte un
   taux est donc simulé. */
const isSim = (row, m) => {
  const a = row.cells[m];
  if (!a) return false;
  if (!row.baseCells) return (a.pct || 0) !== 0;
  const b = row.baseCells[m];
  if (!b) return false;
  return (a.pct || 0) !== (b.pct || 0);
};

export default function PlanningTimeline({
  rows, syntheticRows = [], years = [], closedThrough,
  windowOffset = null, onWindowOffset = null, onWindow = null,
  sortCol, sortDir, onSort, group = 'person', causeGroups = [],
  rowAction = null, syntheticAction = null, addSynthetic = null, deptAction = null,
}) {
  const byCause = group === 'cause';
  const byDept = group === 'dept';
  const boxRef = useRef(null);
  /* Repérage dans la grille : le mois survolé s'éclaire sur TOUTES les lignes,
     la ligne survolée sur toute sa largeur. Vingt-quatre colonnes sans repère,
     on perd sa colonne dès qu'on descend de trois lignes. */
  const [hovM, setHovM] = useState(null);

  /* Combien d'années tiennent ? La largeur décide — jamais un réglage. Deux
     colonnes de libellés (172 + 124) plus une piste lisible par année. */
  const [vis, setVis] = useState(years.length > 1 ? 2 : 1);
  /* La fenêtre d'années peut être PILOTÉE par la card (standard de carte : la
     navigation de période vit dans la toolbar, pas dans le corps). Sans ces
     props, la frise garde sa propre fenêtre. */
  const [offState, setOffState] = useState(0);
  const off = windowOffset == null ? offState : windowOffset;
  const setOff = onWindowOffset || setOffState;
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return undefined;
    const measure = () => {
      const w = el.clientWidth;
      // Pas encore mesuré (premier rendu, conteneur caché) : on GARDE le défaut
      // à deux ans plutôt que de retomber à un — sinon la frise clignote d'une
      // année à deux au montage.
      if (!w) return;
      const room = w - 172 - 124 - 26 - 20;      // libellés + gouttière + gaps
      setVis(Math.min(years.length, room >= 2 * 330 ? 2 : 1));
    };
    measure();
    if (!window.ResizeObserver) return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [years.length]);
  const maxOff = Math.max(0, years.length - vis);
  const shown = years.slice(Math.min(off, maxOff), Math.min(off, maxOff) + vis);
  /* La frise mesure elle-même combien d'années tiennent (`vis`) : elle REND
     COMPTE de sa fenêtre à la card, qui affiche la navigation de période. */
  useEffect(() => {
    if (!onWindow) return;
    onWindow({ maxOff, label: vis === 2 ? p('2 ans') : String(shown[0] ?? ''), count: years.length });
  }, [onWindow, maxOff, vis, shown[0], years.length, p]);
  const base0 = years.indexOf(shown[0]) * 12;    // décalage dans les cellules
  const N = shown.length * 12;

  /* Mois clos depuis le DÉBUT DE LA FENÊTRE affichée : la borne est une date,
     pas un rang — sur N+1 elle vaut zéro, sur N elle vaut le mois de clôture. */
  const closedM = (() => {
    if (!closedThrough || !shown.length) return 0;
    const first = new Date(shown[0], 0, 1);
    const last = new Date(shown[shown.length - 1], 11, 1);
    if (closedThrough < first) return 0;
    if (closedThrough >= last) return N;
    return (closedThrough.getFullYear() - shown[0]) * 12 + closedThrough.getMonth() + 1;
  })();

  /* Lignes par DÉPARTEMENT : l'EPT du mois, somme des taux ÷ 100. Les lignes
     synthétiques y entrent par leur propre département — « À définir » si vide,
     jamais rattachées au hasard. */
  const deptRows = useMemo(() => {
    if (!byDept) return [];
    const m = new Map();
    const push = (d, row) => { const k = d || p('À définir'); if (!m.has(k)) m.set(k, []); m.get(k).push(row); };
    rows.forEach((r) => push(r.department, r));
    syntheticRows.forEach((r) => push(r.department, r));
    const total = years.length * 12;
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0], 'fr')).map(([d, rs]) => ({
      d,
      n: rs.filter((r) => !r.synthetic).length,
      cells: Array.from({ length: total }, (_, i) =>
        rs.reduce((a, r) => a + ((r.cells[i] && r.cells[i].pct) || 0), 0) / 100),
      base: Array.from({ length: total }, (_, i) =>
        rs.reduce((a, r) => a + ((r.baseCells && r.baseCells[i] && r.baseCells[i].pct) || 0), 0) / 100),
    }));
  }, [byDept, rows, syntheticRows, years.length]);

  const head = (key, label, cls) => (
    <button
      type="button"
      className={`${cls} pt-name--head pt-sort${sortCol === key ? ' is-active' : ''}`}
      onClick={() => onSort && onSort(key)}
    >
      {label}
      <span className="pt-sort__ind">{sortCol === key ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}</span>
    </button>
  );

  const bands = () => (
    <span className="pt-yband">
      {shown.map((y, k) => (
        <span key={y} className={`pt-y${k === 0 ? ' pt-y--first' : ''}${k === shown.length - 1 ? ' pt-y--last' : ''}`}>{y}</span>
      ))}
    </span>
  );
  const seps = () => shown.slice(1).map((y, k) => (
    <span key={`s${y}`} className="pt-ysep" style={{ left: `${((k + 1) * 12 / N) * 100}%` }} aria-hidden="true" />
  ));

  const renderRow = (row, key, synthetic) => {
    const name = synthetic
      ? (row.label || p('Ligne synthétique'))
      : `${row.last_name || ''} ${row.first_name || ''}`.trim();
    const dept = synthetic ? (row.department || p('À définir')) : (row.department || '—');
    return (
      <div className="pt-row" key={key}>
        <span className="pt-name" title={name}>{name}</span>
        <span className="pt-dept" title={dept}>{dept}</span>
        {/* Entrée / sortie : la frise dit QUAND ça bouge, ces deux dates disent
            depuis quand et jusqu'à quand la personne compte. */}
        <span className="pt-date">{synthetic ? '—' : fmtMonthYear(row.entry_date)}</span>
        <span className="pt-date">{synthetic ? '—' : fmtMonthYear(row.termination_date)}</span>
        <span className="pt-act hr-page__no-print">
          {synthetic
            ? (syntheticAction ? syntheticAction(row) : null)
            : (rowAction ? rowAction(row) : null)}
        </span>
        <span className="pt-track">
          {Array.from({ length: N }, (_, m) => {
            const c = row.cells[base0 + m] || { pct: 0 };
            const pct = Math.round(c.pct || 0);
            const sim = isSim(row, base0 + m);
            const prev = row.cells[base0 + m - 1];
            /* Un changement de taux se marque par un FILET à la frontière, pas
               par un aplat sur le mois (2026-08-26) : l'aplat peignait des mois
               entiers en ardoise et se confondait avec le couloir de
               simulation, alors qu'on cherche l'instant où ça bascule. */
            const chg = m > 0 && prev && pct !== Math.round(prev.pct || 0);
            const zero = pct === 0;
            const cls = ['pt-cell', sim ? 'is-sim' : '', chg ? 'is-chg' : '',
                         zero ? 'is-zero' : '', m === hovM ? 'is-hovm' : ''].filter(Boolean).join(' ');
            return (
              <span key={m} className={cls}
                    style={{ left: `${(m / N) * 100}%`, width: `${100 / N}%` }}
                    onMouseEnter={() => setHovM(m)}
                    title={`${MOIS[m % 12]} ${shown[Math.floor(m / 12)]} · ${pct} %`}>
                <b>{zero ? '—' : pct}</b>
              </span>
            );
          })}
          {closedM > 0 && (
            <span className="pt-closed" style={{ width: `${(closedM / N) * 100}%` }} aria-hidden="true" />
          )}
          {seps()}
        </span>
      </div>
    );
  };

  const renderDept = (g) => (
    <div className="pt-row" key={g.d}>
      <span className="pt-name" title={g.d}>{g.d}</span>
      <span className="pt-dept">{g.n} {g.n > 1 ? p('pers.') : p('pers.')}</span>
      <span className="pt-act hr-page__no-print">{deptAction ? deptAction(g.d) : null}</span>
      <span className="pt-track">
        {Array.from({ length: N }, (_, m) => {
          const v = g.cells[base0 + m] || 0;
          const prev = m > 0 ? (g.cells[base0 + m - 1] || 0) : v;
          const eps = 0.005;
          const cls = ['pt-cell', v === 0 ? 'is-zero' : '',
                       v > prev + eps ? 'is-up' : (v < prev - eps ? 'is-down' : ''),
                       m === hovM ? 'is-hovm' : ''].filter(Boolean).join(' ');
          return (
            <span key={m} className={cls}
                  style={{ left: `${(m / N) * 100}%`, width: `${100 / N}%` }}
                  onMouseEnter={() => setHovM(m)}
                  title={`${MOIS[m % 12]} ${shown[Math.floor(m / 12)]} · ${v.toFixed(2)} EPT`}>
              <b>{v === 0 ? '—' : v.toFixed(1)}</b>
            </span>
          );
        })}
        {closedM > 0 && (
          <span className="pt-closed" style={{ width: `${(closedM / N) * 100}%` }} aria-hidden="true" />
        )}
        {seps()}
      </span>
    </div>
  );

  return (
    <div className="pt" ref={boxRef}>
      {!byCause && (
        <div className="pt-leg">
          {byDept ? (
            <>
              <span><i className="pt-sw pt-sw--up" />{p('L’effectif monte')}</span>
              <span><i className="pt-sw pt-sw--dn" />{p('L’effectif baisse')}</span>
            </>
          ) : (
            <>
              <span><i className="pt-sw pt-sw--sim" />{p('Simulation')}</span>
            </>
          )}
        </div>
      )}
      <div className="pt-scroll">
        <div className={`pt-in${byCause ? ' pt-in--cause' : byDept ? ' pt-in--dept' : ' pt-in--person'}`}
             onMouseLeave={() => setHovM(null)}>
          <div className="pt-head">
            {byCause
              ? <><span className="pt-name pt-name--head">{p('Mouvement')}</span>
                  <span className="pt-dept pt-name--head">{p('Département')}</span>
                  <span className="pt-dept pt-name--head">{p('Valeur')}</span></>
              : byDept
                ? <><span className="pt-name pt-name--head">{p('Département')}</span>
                    <span className="pt-dept pt-name--head">{p('Effectif')}</span>
                    <span className="pt-act" aria-hidden="true" /></>
                : <>{head('last_name_sort', p('Collaborateur'), 'pt-name')}
                    {head('department', p('Département'), 'pt-dept')}
                    {head('entry_date', p('Entrée'), 'pt-date')}
                    {head('termination_date', p('Sortie'), 'pt-date')}
                    <span className="pt-act" aria-hidden="true" /></>}
            <span className="pt-axis">
              {bands()}
              <span className="pt-ticks">
                {Array.from({ length: N }, (_, i) => (
                  <span key={i} className={`pt-tick${i < closedM ? ' is-closed' : ''}${i === hovM ? ' is-hovm' : ''}`}>{MOIS[i % 12]}</span>
                ))}
              </span>
            </span>
          </div>
          {byCause ? (
            causeGroups.length === 0
              ? <div className="pt-none">{p('Aucun mouvement sur cet exercice.')}</div>
              : causeGroups.map((g) => (
                <React.Fragment key={g.key}>
                  <div className="pt-grp">{g.label}</div>
                  {g.rows.map((r) => (
                    <div className="pt-row" key={r.id}>
                      <span className="pt-name" title={r.name}>{r.name}</span>
                      <span className="pt-dept" title={r.dept}>{r.dept}</span>
                      <span className="pt-dept" title={r.detail}>{r.detail}</span>
                      <span className="pt-track">
                        {closedM > 0 && (
                          <span className="pt-closed" style={{ width: `${(closedM / N) * 100}%` }} aria-hidden="true" />
                        )}
                        <span className="pt-bar"
                              style={{ left: `${(r.from / N) * 100}%`,
                                       width: `${((r.to - r.from + 1) / N) * 100}%`,
                                       background: `var(${g.color})` }} />
                        {seps()}
                      </span>
                    </div>
                  ))}
                </React.Fragment>
              ))
          ) : byDept ? (
            deptRows.map(renderDept)
          ) : (
            <>
              {rows.map((r) => renderRow(r, r.id, false))}
              {(syntheticRows.length > 0 || addSynthetic) && (
                <div className="pt-grp">
                  {p('Positions et réductions')}
                  {addSynthetic && <span className="pt-grp__add hr-page__no-print">{addSynthetic}</span>}
                </div>
              )}
              {syntheticRows.map((r) => renderRow(r, r.id, true))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
